/// <amd-module name="WorkItemsHub/Utils/UrlUtils" />
import { routeUrl } from "VSS/Platform/Location";
import { IVssPageContext } from "VSS/Platform/Context";
import { WorkItemsHubRouteId, CreateWorkItemRouteId, OpenWorkItemRouteId, QueriesRouteId } from "Constants";
import { ActionUrl, ActionParameters } from "WorkItemsHub/Constants";
import * as Resources from "WorkItemsHub/Resources";

/**
 * Gets the current url's hash string
 */
export function getCurrentHashString(): string {
    let hash = window.location.hash;
    if (hash && hash[0] === "#") {
        hash = hash.substr(1);
    }
    return hash;
}

export function deserializeState(state: string): { [key: string]: string } {
    const result: { [key: string]: string } = {};

    let tokens = (state || "").split("&");
    tokens.forEach(token => {
        let parts = token.split("=", 2);
        if (parts.length === 2) {
            let key = parts[0];
            if (key === "_a") {
                key = "action";
            }

            // Replace "+" character with %20.
            const value = parts[1].replace(/\+/g, "%20");

            try {
                result[key] = decodeURIComponent(value);
            } catch (error) {
                result[key] = value;
            }
        }
    });

    return result;
}

/**
 * Gets the URL for the workitem hub
 */
export function getWorkItemHubUrl(pageContext: IVssPageContext): string {
    return routeUrl(pageContext, WorkItemsHubRouteId);
}

/**
 * Gets the URL for the queries hub
 * @param fragment fragment (e.g. #_a=query&id=<guid>)
 */
export function getQueriesHubUrl(pageContext: IVssPageContext, projectName: string, fragment: string): string {
    const url = routeUrl(pageContext, QueriesRouteId, {
        project: projectName
    });
    return `${url}?${fragment}`;
}

/**
 * Gets the edit URL for the given workitem
 * @param workItemId ID of the workitem for which the edit URL is to be returned
 */
export function getEditWorkItemUrl(pageContext: IVssPageContext, projectName: string, workItemId: number | string): string {
    const url = projectName
        ? routeUrl(pageContext, OpenWorkItemRouteId, {
              project: projectName,
              id: `${workItemId}`
          })
        : "";

    return url;
}

/**
 * Gets the URL to create a workitem of the given type
 * @param pageContext page context
 * @param projectName project name
 * @param workItemType Type the workitem to be created
 */
export function getCreateWorkItemUrl(pageContext: IVssPageContext, projectName: string, workItemType: string): string {
    const url = routeUrl(pageContext, CreateWorkItemRouteId, {
        project: projectName
    });

    return `${url}/${workItemType}`;
}

/**
 * Gets the URL for the Import Work Items
 */
export function getImportWorkItemsUrl(pageContext: IVssPageContext, projectName: string): string {
    const state = {
        [ActionUrl.ACTION]: ActionUrl.ACTION_QUERY,
        [ActionParameters.IMPORTDATA]: "csv"
    };

    const queryParam = serializeState(state);
    return `${getQueriesHubUrl(pageContext, projectName, queryParam)}`;
}

/**
 * Gets the URL for the Recycle bin
 */
export function getRecycleBinUrl(pageContext: IVssPageContext, projectName: string): string {
    const state = {
        [ActionUrl.ACTION]: ActionUrl.ACTION_QUERY,
        [ActionParameters.PATH]: Resources.RecycleBin
    };

    const recycleBinQueryPath = serializeState(state);
    return `${getQueriesHubUrl(pageContext, projectName, recycleBinQueryPath)}`;
}

/**
 * Gets the URL for queries hub with temp query id
 * @param tempQueryId id of temporary query
 */
export function getTempQueryUrl(pageContext: IVssPageContext, projectName: string, tempQueryId: string): string {
    const state = {
        [ActionUrl.ACTION]: ActionUrl.ACTION_QUERY,
        [ActionParameters.TEMPQUERYID]: tempQueryId
    };
    const queryParam = serializeState(state);
    return `${getQueriesHubUrl(pageContext, projectName, queryParam)}`;
}

function serializeState(state: { [key: string]: any }): string {
    var serialized = [];
    for (let key in state) {
        let value = state[key];
        if (value !== null && typeof value !== "undefined") {
            var entry = key === "action" ? "_a" : key;
            serialized.push(entry + "=" + encodeURIComponent(value));
        }
    }
    return serialized.join("&");
}
